import { useState, useMemo } from 'react'
import { startOfWeek, endOfWeek, eachDayOfInterval, isWeekend, format } from 'date-fns'
import { de } from 'date-fns/locale'
import { LoadingState, ErrorState, EmptyState, Card } from '@/components/ui'
import { Users, AlertTriangle } from 'lucide-react'
import { DispositionCalendarHeader } from './DispositionCalendarHeader.tsx'
import { DispositionCalendarEmployeeRow } from './DispositionCalendarEmployeeRow.tsx'
import { DispositionCalendarLegend } from './DispositionCalendarLegend.tsx'
import { useDispositionTimeline } from './useDispositionTimeline.ts'
import {
  CALENDAR_DAY_START_HOUR,
  CALENDAR_HOURS_IN_RANGE,
} from './calendar-config.ts'
import {
  CalendarFilterPanel,
  emptyCalendarFilter,
  type CalendarFilterState,
} from './CalendarFilterPanel.tsx'
import { useCalendarDepartments } from './useCalendarFilterData.ts'

export interface DispositionCalendarProps {
  /** Phase 1: week only; month architecturally prepared via this prop */
  mode?: 'week' | 'month'
  /** Initial week start; defaults to current week */
  from?: Date
  /** Initial week end; defaults to current week */
  to?: Date
  employeeIds?: number[]
  showHeader?: boolean
  showEmployeeImages?: boolean
  /** Show the filter panel (Abteilungen / Mitarbeiter / Typen) above the calendar. Default: true */
  showFilter?: boolean
  visiblePlanningTypes?: string[]
  /** When set, CUSTOM events are filtered to only those matching these planningAdditionalIds */
  visiblePlanningAdditionalIds?: number[]
  /** Called when the user navigates to a different week */
  onWeekChange?: (from: Date, to: Date) => void
  /** Called when the user clicks on a calendar event */
  onEventClick?: (event: import('@shared/types').DispoCalendarEvent) => void
  /** Optional tenant ID – when provided, external deployments for that tenant are shown */
  tenantId?: string
  /**
   * External data mode — when provided, skips the internal useDispositionTimeline fetch.
   * The calendar filters the provided employees to the current visible week client-side.
   * Useful when the parent already fetched the data and wants calendar and list to share it.
   */
  externalData?: {
    employees: import('@shared/types').DispoCalendarEmployee[]
    isLoading?: boolean
    error?: Error | null
  }
}

/** Derive week start/end (Mon–Sun) for a given date */
function weekRange(date: Date): { from: Date; to: Date } {
  return {
    from: startOfWeek(date, { weekStartsOn: 1 }),
    to: endOfWeek(date, { weekStartsOn: 1 }),
  }
}

export function DispositionCalendar({
  mode = 'week',
  from: fromProp,
  employeeIds: employeeIdsProp,
  showHeader = true,
  showEmployeeImages = true,
  showFilter = true,
  visiblePlanningTypes: visiblePlanningTypesProp,
  visiblePlanningAdditionalIds: visiblePlanningAdditionalIdsProp,
  onWeekChange,
  onEventClick,
  tenantId,
  externalData,
}: DispositionCalendarProps) {
  const initial = weekRange(fromProp ?? new Date())
  const [from, setFrom] = useState<Date>(initial.from)
  const [to, setTo] = useState<Date>(initial.to)

  // Internal filter state (managed here so all calendar usages share the filter panel)
  const [filter, setFilter] = useState<CalendarFilterState>(emptyCalendarFilter)
  const { data: departments = [] } = useCalendarDepartments()

  // Resolve employeeIds from filter (departments + direct selection) merged with prop
  const employeeIds = useMemo<number[] | undefined>(() => {
    if (!showFilter) return employeeIdsProp

    const deptEmpIds =
      filter.departmentIds.length > 0
        ? [
            ...new Set(
              departments
                .filter((d) => filter.departmentIds.includes(d.id))
                .flatMap((d) => d.employeeIds),
            ),
          ]
        : null
    const manualIds = filter.employeeIds.length > 0 ? filter.employeeIds : null

    let filterIds: number[] | undefined
    if (!deptEmpIds && !manualIds) filterIds = undefined
    else if (deptEmpIds && !manualIds) filterIds = deptEmpIds
    else if (!deptEmpIds && manualIds) filterIds = manualIds
    else {
      const deptSet = new Set(deptEmpIds!)
      filterIds = manualIds!.filter((id) => deptSet.has(id))
    }

    // Intersect with prop-provided employeeIds if both are present
    if (employeeIdsProp && filterIds) {
      const filterSet = new Set(filterIds)
      return employeeIdsProp.filter((id) => filterSet.has(id))
    }
    return employeeIdsProp ?? filterIds
  }, [showFilter, filter.departmentIds, filter.employeeIds, departments, employeeIdsProp])

  // Effective planning type filters: internal filter takes precedence over props
  const visiblePlanningTypes =
    showFilter && filter.planningTypes.length > 0 ? filter.planningTypes : visiblePlanningTypesProp
  const visiblePlanningAdditionalIds =
    showFilter && filter.planningAdditionalIds.length > 0
      ? filter.planningAdditionalIds
      : visiblePlanningAdditionalIdsProp

  // Internal fetch — disabled when externalData is provided (no network request in that case).
  const internalTimeline = useDispositionTimeline({ from, to, employeeIds, tenantId, enabled: !externalData })
  const data = externalData ? null : internalTimeline.data
  const isLoading = externalData ? (externalData.isLoading ?? false) : internalTimeline.isLoading
  const error = externalData ? (externalData.error ?? null) : (internalTimeline.error ?? null)

  // Workdays only (Mon–Fri) for week mode
  const days = useMemo(() => {
    const all = eachDayOfInterval({ start: from, end: to })
    const workDays = mode === 'week' ? all.filter((d) => !isWeekend(d)) : all
    return workDays.map((d) => ({
      date: d,
      key: format(d, 'yyyy-MM-dd'),
      label: format(d, 'EEE d.M.', { locale: de }),
    }))
  }, [from, to, mode])

  // Resolve raw employee list: external data (filtered to current week) or internal fetch.
  const rawEmployees = useMemo(() => {
    if (externalData) {
      // Filter external employees' events to the current visible week to avoid rendering
      // events from other weeks outside the visible columns.
      const fromTs = Math.floor(from.getTime() / 1000)
      const toTs = Math.floor(to.getTime() / 1000)
      return externalData.employees.map((emp) => ({
        ...emp,
        events: emp.events.filter((e) => {
          const evStart = e.startTs
          const evEnd = e.endTs ?? e.startTs
          return evStart <= toTs && evEnd >= fromTs
        }),
      }))
    }
    return data?.employees ?? []
  }, [externalData, data, from, to])

  // Filter employees by visiblePlanningTypes / visiblePlanningAdditionalIds if requested
  const employees = useMemo(() => {
    const hasTypeFilter = !!visiblePlanningTypes?.length
    const hasAdditionalFilter = !!visiblePlanningAdditionalIds?.length
    if (!hasTypeFilter && !hasAdditionalFilter) return rawEmployees
    return rawEmployees.map((emp) => ({
      ...emp,
      events: emp.events.filter((e) => {
        if (e.type !== 'CUSTOM') {
          return !hasTypeFilter || visiblePlanningTypes!.includes(e.type)
        }
        // CUSTOM event: filter by planningAdditionalId when specified
        if (hasAdditionalFilter) {
          return !!e.planningAdditionalId && visiblePlanningAdditionalIds!.includes(e.planningAdditionalId)
        }
        // No additional filter: show CUSTOM events only if CUSTOM is in visibleTypes or no type filter
        return !hasTypeFilter || visiblePlanningTypes!.includes('CUSTOM')
      }),
    }))
  }, [rawEmployees, visiblePlanningTypes, visiblePlanningAdditionalIds])

  function handleNavigate(newFrom: Date, newTo: Date) {
    setFrom(newFrom)
    setTo(newTo)
    onWeekChange?.(newFrom, newTo)
  }

  if (isLoading) return <LoadingState label="Kalender wird geladen…" />
  if (error) {
    return (
      <ErrorState
        title="Kalender konnte nicht geladen werden"
        description="Die allgemeine TANSS-Verbindung ist nicht verfügbar. Bitte unter Einstellungen → Integrationen die TANSS-Konfiguration prüfen."
        details={error.message}
      />
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {showFilter && <CalendarFilterPanel filter={filter} onChange={setFilter} />}
      {showHeader && (
        <DispositionCalendarHeader
          from={from}
          to={to}
          onNavigate={handleNavigate}
        />
      )}

      {(data?.warning) && (
        <div className="flex items-start gap-2 rounded-md bg-yellow-50 border border-yellow-200 px-3 py-2 text-xs text-yellow-800">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <details className="w-full">
            <summary className="cursor-pointer font-medium">TANSS-Warnung: Timeline-Daten konnten nicht geladen werden</summary>
            <span className="block mt-1 text-yellow-700">{data.warning}</span>
          </details>
        </div>
      )}

      <Card>
        {/* Day header */}
        <div className="grid grid-cols-[160px_1fr] border-b border-gray-200">
          <div className="px-3 py-2 bg-gray-50 border-r border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Mitarbeiter
          </div>
          <div
            className="grid border-r border-gray-200 last:border-r-0"
            style={{ gridTemplateColumns: `repeat(${days.length}, 1fr)` }}
          >
            {days.map((day) => (
              <div
                key={day.key}
                className="py-2 px-1 text-center text-xs font-medium text-gray-600 border-r border-gray-100 last:border-r-0"
              >
                {day.label}
              </div>
            ))}
          </div>
        </div>

        {/* Time axis row — hour labels once, shared across all employee rows */}
        <div className="grid grid-cols-[160px_1fr] border-b border-gray-100">
          <div className="bg-gray-50 border-r border-gray-100" />
          <div
            className="grid"
            style={{ gridTemplateColumns: `repeat(${days.length}, 1fr)` }}
          >
            {days.map((day) => (
              <div
                key={day.key}
                className="flex border-r border-gray-100 last:border-r-0"
                style={{ height: '20px' }}
              >
                {/* Left wing — aligns with employee row wing */}
                <div className="w-4 shrink-0 bg-gray-50 border-r border-gray-200" />

                {/* Label area — every 2nd hour, clipped at boundaries */}
                <div className="flex-1 relative overflow-hidden">
                  {Array.from({ length: CALENDAR_HOURS_IN_RANGE + 1 }, (_, i) => {
                    const hour = CALENDAR_DAY_START_HOUR + i
                    const isFirst = i === 0
                    const isLast = i === CALENDAR_HOURS_IN_RANGE
                    if (!isFirst && !isLast && i % 2 !== 0) return null
                    const pct = (i / CALENDAR_HOURS_IN_RANGE) * 100
                    return (
                      <span
                        key={hour}
                        className="absolute top-1/2 -translate-y-1/2 text-[9px] text-gray-400 pointer-events-none"
                        style={
                          isFirst
                            ? { left: '2px', transform: 'translateY(-50%)' }
                            : isLast
                            ? { right: '2px', transform: 'translateY(-50%)' }
                            : { left: `${pct}%`, transform: 'translateX(-50%) translateY(-50%)' }
                        }
                      >
                        {String(hour).padStart(2, '0')}:00
                      </span>
                    )
                  })}
                </div>

                {/* Right wing — aligns with employee row wing */}
                <div className="w-4 shrink-0 bg-gray-50 border-l border-gray-200" />
              </div>
            ))}
          </div>
        </div>

        {/* Employee rows */}
        {employees.length === 0 ? (
          <EmptyState
            icon={<Users className="w-8 h-8" />}
            title="Keine Mitarbeiter in TANSS gefunden"
            description="Es wurden keine aktiven Mitarbeiter über die allgemeine TANSS-Verbindung geladen. Bitte unter Einstellungen → Integrationen die TANSS-Konfiguration prüfen."
          />
        ) : (
          employees.map((emp) => (
            <DispositionCalendarEmployeeRow
              key={emp.id}
              employee={emp}
              days={days}
              showAvatar={showEmployeeImages}
              onEventClick={onEventClick}
            />
          ))
        )}
      </Card>

      <DispositionCalendarLegend />
    </div>
  )
}
